import * as React from 'react';
import {
  motion,
  useReducedMotion,
  type MotionStyle,
  type Transition,
  type Variants,
} from 'framer-motion';

export const WEIGHTS = ["thin", "light", "regular", "bold", "duotone"] as const;
export type Weight = (typeof WEIGHTS)[number];

export const STROKE_WIDTH: Record<Weight, number> = {
  thin: 8, light: 12, regular: 16, bold: 24, duotone: 16,
};

export type Part = [tag: string, attrs: Record<string, string | number>];

export type IconGeometry = {
  regular: Part[];
  thin?: Part[];
  light?: Part[];
  bold?: Part[];
  duotone?: Part[];
  backdrop?: Partial<Record<Weight, Part[]>>;
  maps?: Partial<Record<Weight, (number | null)[]>>;
  divergent?: Weight[];
};

export type PartAnimation = {
  rotate?: number[];
  scale?: number[];
  scaleX?: number[];
  scaleY?: number[];
  x?: number[];
  y?: number[];
  opacity?: number[];
  pathLength?: number[];
  origin?: [number, number];
  delay?: number;
  duration?: number;
  ease?: Transition['ease'] | Transition['ease'][];
  times?: number[];
  spring?: { stiffness?: number; damping?: number; mass?: number; velocity?: number };
};

export type Choreography = {
  parts?: Record<number, PartAnimation>;
  whole?: PartAnimation;
  duration?: number;
};

export type Trigger = 'hover' | 'click' | 'in-view' | 'loop' | 'none';

export type AnimatedIconHandle = {
  play: () => void;
  stop: () => void;
};

export type AnimatedIconProps = Omit<
  React.SVGProps<SVGSVGElement>,
  | 'ref'
  | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
  | 'onDrag' | 'onDragStart' | 'onDragEnd'
  | 'values'
> & {
  size?: number | string;
  weight?: Weight;
  trigger?: Trigger;
  speed?: number;
};

export type IconComponent = React.ForwardRefExoticComponent<
  AnimatedIconProps & React.RefAttributes<AnimatedIconHandle>
>;

export type IconSpec = {
  geometry: IconGeometry;
  stroke: Choreography;
};

type InternalProps = AnimatedIconProps & { spec: IconSpec };

const DEFAULT_DURATION = 0.96;
const IDENTITY: Record<string, number> = {
  rotate: 0, scale: 1, scaleX: 1, scaleY: 1, x: 0, y: 0, opacity: 1, pathLength: 1,
};

const ANIMATABLE = ["rotate", "scale", "scaleX", "scaleY", "x", "y", "opacity", "pathLength"] as const;

function buildVariants(
  anim: PartAnimation,
  duration: number,
  speed: number,
  repeat: boolean,
): Variants {
  const rest: Record<string, unknown> = {};
  const active: Record<string, unknown> = {};

  for (const key of ANIMATABLE) {
    const frames = anim[key];
    if (!frames?.length) continue;
    rest[key] = frames[frames.length - 1] ?? IDENTITY[key];
    active[key] = frames;
  }

  const delay = (anim.delay ?? 0) / (speed || 1);
  const loop = repeat ? { repeat: Infinity, repeatDelay: 0.45 / (speed || 1) } : {};

  if (anim.spring) {
    const { velocity, ...restSpring } = anim.spring;
    active.transition = {
      type: "spring",
      stiffness: 220,
      damping: 14,
      mass: 1,
      ...restSpring,
      ...(velocity !== undefined ? { velocity: velocity * (speed || 1) } : {}),
      delay,
      ...loop,
    } satisfies Transition;
  } else {
    const d = anim.duration && anim.duration > 0 ? anim.duration : duration;
    active.transition = {
      duration: d / (speed || 1),
      delay,
      ease: anim.ease ?? "easeInOut",
      ...(anim.times ? { times: anim.times } : {}),
      ...loop,
    } as Transition;
  }

  rest.transition = {
    type: "spring",
    stiffness: 260,
    damping: 26,
    mass: 0.7,
  } satisfies Transition;

  return { rest, active } as Variants;
}

function originStyle(anim: PartAnimation): MotionStyle {
  const [ox, oy] = anim.origin ?? [128, 128];
  return { transformBox: "view-box" as const, originX: `${ox}px`, originY: `${oy}px` };
}

export function resolveWeight(geometry: IconGeometry, weight: Weight) {
  const divergent = geometry.divergent?.includes(weight) ?? false;
  const parts =
    (weight === "regular" ? geometry.regular : geometry[weight]) ?? geometry.regular;
  const map = geometry.maps?.[weight];
  const backdrop = geometry.backdrop?.[weight];
  return { parts, map, backdrop, divergent };
}

export const AnimatedIcon = React.forwardRef<AnimatedIconHandle, InternalProps>(
  function AnimatedIcon(
    {
      spec,
      size = 24, weight = "regular", trigger = "hover", speed = 1,
      onMouseEnter, onMouseLeave, onClick, style,
      fill = "none",
      ...rest
    },
    ref,
  ) {
    const reduced = useReducedMotion();
    const [active, setActive] = React.useState(false);

    React.useImperativeHandle(ref, () => ({
      play: () => setActive(true),
      stop: () => setActive(false),
    }), []);

    const { parts, map, backdrop, divergent } = resolveWeight(spec.geometry, weight);
    const choreo = spec.stroke;
    const duration = choreo.duration && choreo.duration > 0 ? choreo.duration : DEFAULT_DURATION;
    const looping = trigger === "loop";

    const animateState = reduced ? "rest" : looping || active ? "active" : "rest";

    const wholeVariants = React.useMemo(
      () => (choreo.whole ? buildVariants(choreo.whole, duration, speed, looping) : null),
      [choreo.whole, duration, speed, looping],
    );

    const svgStyle: MotionStyle = {
      overflow: "hidden",
      ...(style as MotionStyle),
    };

    const handlers = {
      onMouseEnter: (e: React.MouseEvent<SVGSVGElement>) => {
        if (trigger === "hover") setActive(true);
        onMouseEnter?.(e);
      },
      onMouseLeave: (e: React.MouseEvent<SVGSVGElement>) => {
        if (trigger === "hover") setActive(false);
        onMouseLeave?.(e);
      },
      onClick: (e: React.MouseEvent<SVGSVGElement>) => {
        if (trigger === "click") {
          setActive(true);
          window.setTimeout(() => setActive(false), (duration / (speed || 1)) * 1000 + 60);
        }
        onClick?.(e);
      },
    };

    const drawing = (
      <>
        {backdrop?.map(([tag, attrs], i) =>
          React.createElement(tag, { key: `bd-${i}`, ...attrs, fill: "currentColor", stroke: "none" }),
        )}

        {parts.map((_, renderIndex) => {
          const sourceIndex = map
            ? map.findIndex((target) => target === renderIndex)
            : renderIndex;
          const anim = !divergent && sourceIndex >= 0 ? choreo.parts?.[sourceIndex] : undefined;
          const [tag, attrs] = parts[renderIndex];

          if (!anim) return React.createElement(tag, { key: renderIndex, ...attrs });

          const v = buildVariants(anim, duration, speed, looping);
          const MotionTag = (motion as unknown as Record<string, React.ElementType>)[tag];

          return (
            <MotionTag
              key={renderIndex}
              {...attrs}
              style={originStyle(anim)}
              variants={{ rest: v.rest, active: v.active }}
            />
          );
        })}
      </>
    );

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 256 256"
        width={size}
        height={size}
        fill={fill}
        stroke="currentColor"
        strokeWidth={STROKE_WIDTH[weight]}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={svgStyle}
        initial="rest"
        animate={animateState}
        {...(trigger === "in-view"
          ? { whileInView: "active", viewport: { once: false, amount: 0.6 } }
          : null)}
        {...handlers}
        {...rest}
      >
        {wholeVariants && choreo.whole ? (
          <motion.g
            style={originStyle(choreo.whole)}
            variants={{ rest: wholeVariants.rest, active: wholeVariants.active }}
          >
            {drawing}
          </motion.g>
        ) : (
          drawing
        )}
      </motion.svg>
    );
  },
);

export function createAnimatedIcon(
  displayName: string,
  geometry: IconGeometry,
  stroke: Choreography,
) {
  const spec: IconSpec = { geometry, stroke };
  const Icon = React.forwardRef<AnimatedIconHandle, AnimatedIconProps>(function Icon(props, ref) {
    return <AnimatedIcon ref={ref} spec={spec} {...props} />;
  });
  Icon.displayName = displayName;
  return Icon;
}
